import { Card, Checkbox, Stack, Text } from "@mantine/core";
import { AtIcon, ChatIcon, DeviceMobileIcon } from "@phosphor-icons/react";
import { ComponentProps } from "react";
import { Control, FieldValues, Path, useController } from "react-hook-form";
import z from "zod";

// Schema de validação Zod
export const contactMethodsSchema = z.array(z.enum(["sms", "email", "whatsapp"])).default([]);
export type ContactMethodsSchema = z.infer<typeof contactMethodsSchema>;

// Configuração dos métodos de contato
const CONTACT_METHODS = [
  {
    value: "sms" as const,
    label: "Receber notificações via SMS",
    icon: <DeviceMobileIcon size={16} weight="bold" />,
    description: "Mensagens de texto no celular"
  },
  {
    value: "email" as const,
    label: "Receber notificações via Email",
    icon: <AtIcon size={16} weight="bold" />,
    description: "Notificações por correio eletrônico"
  },
  {
    value: "whatsapp" as const,
    label: "Receber notificações via WhatsApp",
    icon: <ChatIcon size={16} weight="bold" />,
    description: "Mensagens via WhatsApp"
  }
] as const;

type ContactMethodsFieldsProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  name: Path<TFieldValues>;
  label?: string;
  description?: string;
  disabled?: boolean;
  required?: boolean;
  variant?: "default" | "compact";
  showIcons?: boolean;
  showDescriptions?: boolean;
  cardProps?: ComponentProps<typeof Card>;
};

function ContactMethodsFieldsImpl<TFieldValues extends FieldValues>({
  control,
  name,
  label = "Métodos de Contato",
  description = "Selecione os métodos de contato preferenciais",
  disabled = false,
  required = false,
  variant = "default",
  showIcons = true,
  showDescriptions = false,
  cardProps,
  ...checkboxGroupProps
}: ContactMethodsFieldsProps<TFieldValues> & Omit<ComponentProps<typeof Checkbox.Group>, "children">) {
  const {
    field,
    fieldState: { error }
  } = useController({
    name,
    control
  });

  const isCompact = variant === "compact";

  return (
    <Card padding={isCompact ? "sm" : "md"} shadow="sm" withBorder {...cardProps}>
      <Checkbox.Group {...field} {...checkboxGroupProps} label={label} description={description} error={error?.message} required={required}>
        <Stack gap={isCompact ? "xs" : "sm"} className={isCompact ? "mt-2" : "mt-3"}>
          {CONTACT_METHODS.map((method) => (
            <div key={method.value} className="flex items-start gap-2">
              <Checkbox
                value={method.value}
                label={
                  <div className="flex items-center gap-2">
                    {showIcons && <span className="text-gray-600 dark:text-gray-400">{method.icon}</span>}
                    <span>{method.label}</span>
                  </div>
                }
                disabled={disabled}
                size={isCompact ? "sm" : "md"}
              />
              {showDescriptions && (
                <Text size="xs" c="dimmed" className="mt-1">
                  {method.description}
                </Text>
              )}
            </div>
          ))}
        </Stack>
      </Checkbox.Group>
    </Card>
  );
}

export const ContactMethodsFields = Object.assign(ContactMethodsFieldsImpl, {
  $schema: contactMethodsSchema
});
